import { createContext, useContext, useState, useEffect } from "react";
import { fetchSiteContent } from "../lib/api";
import * as mock from "../data/mock";

const SiteContentContext = createContext(null);

export const useSiteContent = () => {
  const context = useContext(SiteContentContext);
  if (!context) return mapContent({});
  return context;
};

const mapContent = (data) => {
  const pi = data.personal_info || {};
  const ci = data.contact_info;
  const so = data.social;
  // If admin has saved contact_info / social, respect their values exactly (including empty strings).
  // Only fall back to mock if the section was never set at all (undefined).
  const contact = ci ? {
    email: ci.email ?? "",
    emailWork: ci.emailWork ?? "",
    emailAcademic: ci.emailAcademic ?? "",
    phone: ci.phone ?? "",
    phoneUK: ci.phoneUK ?? "",
    address: ci.address ?? "",
    website: ci.website ?? "",
  } : mock.personalInfo.contact;
  const social = so ? { linkedin: so.linkedin ?? "" } : mock.personalInfo.social;
  return {
    personalInfo: {
      name: pi.name || mock.personalInfo.name,
      title: pi.title || mock.personalInfo.title,
      subtitle: pi.subtitle || mock.personalInfo.subtitle,
      tagline: pi.tagline || mock.personalInfo.tagline,
      shortBio: pi.shortBio || mock.personalInfo.shortBio,
      fullBio: pi.fullBio || mock.personalInfo.fullBio,
      contact,
      social,
    },
    heroTagline: data.hero_tagline || "Strategy · Trade · Development",
    currentRoles: data.current_roles || mock.currentRoles,
    expertise: data.expertise || mock.expertise,
    focusSectors: data.focus_sectors || mock.focusSectors,
    skills: data.skills || [],
    socialLinks: Array.isArray(data.social_links) ? data.social_links : [],
    experience: data.experience || mock.experience,
    education: data.education || mock.education,
    publications: data.publications || mock.publications,
    certifications: data.certifications || mock.certifications,
    memberships: data.memberships || mock.memberships,
    awards: data.awards || mock.awards,
    stats: data.stats || mock.stats,
    images: data.images || mock.images,
    heroImages: Array.isArray(data.hero_images) && data.hero_images.length > 0 ? data.hero_images : [(data.images || mock.images).consulting].filter(Boolean),
    aboutImages: Array.isArray(data.about_images) && data.about_images.length > 0 ? data.about_images : [(data.images || mock.images).diplomatic].filter(Boolean),
    quote: data.quote || "",
    quotes: Array.isArray(data.quotes) ? data.quotes : [],
    favicon: data.favicon || null,
    isPreview: false,
  };
};

const mergePreviewDraft = (liveContent, draft) => {
  if (!draft) return liveContent;
  const merged = { ...liveContent, isPreview: true };
  if (draft.personal_info) {
    const pi = draft.personal_info;
    merged.personalInfo = { ...merged.personalInfo, ...pi, contact: merged.personalInfo.contact, social: merged.personalInfo.social };
  }
  if (draft.contact_info) merged.personalInfo = { ...merged.personalInfo, contact: { ...merged.personalInfo.contact, ...draft.contact_info } };
  if (draft.social) merged.personalInfo = { ...merged.personalInfo, social: { ...merged.personalInfo.social, ...draft.social } };
  if (draft.hero_tagline) merged.heroTagline = draft.hero_tagline;
  if (draft.stats) merged.stats = draft.stats;
  if (draft.images) merged.images = draft.images;
  if (draft.current_roles) merged.currentRoles = draft.current_roles;
  if (draft.expertise) merged.expertise = draft.expertise;
  if (draft.focus_sectors) merged.focusSectors = draft.focus_sectors;
  if (draft.skills) merged.skills = draft.skills;
  if (draft.social_links) merged.socialLinks = draft.social_links;
  if (draft.hero_images) merged.heroImages = draft.hero_images;
  if (draft.about_images) merged.aboutImages = draft.about_images;
  if (draft.quotes) merged.quotes = draft.quotes;
  if (draft.quote !== undefined) merged.quote = draft.quote;
  if (draft.experience) merged.experience = draft.experience;
  if (draft.education) merged.education = draft.education;
  if (draft.publications) merged.publications = draft.publications;
  if (draft.certifications) merged.certifications = draft.certifications;
  if (draft.memberships) merged.memberships = draft.memberships;
  if (draft.awards) merged.awards = draft.awards;
  if (draft.quote) merged.quote = draft.quote;
  return merged;
};

export const SiteContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);

  useEffect(() => {
    fetchSiteContent()
      .then((data) => {
        let mapped = mapContent(data);
        const params = new URLSearchParams(window.location.search);
        if (params.get("preview") === "true") {
          try {
            const draft = JSON.parse(localStorage.getItem("admin_preview_draft") || "null");
            mapped = mergePreviewDraft(mapped, draft);
          } catch {}
        }
        setContent(mapped);
      })
      .catch(() => setContent(mapContent({})));
  }, []);

  return (
    <SiteContentContext.Provider value={content || mapContent({})}>
      {children}
    </SiteContentContext.Provider>
  );
};
